import { Injectable } from '@angular/core';
import { StoreService } from '@experian-digital/affordability-library';
import { APP_ROUTES } from '@portal-app/advisor/routes';
import { Application, StepData, StepDataStatus, TabConfig } from '@portal-shared/models/application';
import { Department } from '@portal-shared/models/department';
import { DepartmentFeatures } from '@portal-shared/models/enums/department-features';
import { CompletedApplicationStatus } from '@portal-shared/models/enums/features.enum';
import { STORE_FIELDS } from '@portal-store/models/store.model';

@Injectable({
  providedIn: 'root',
})
export class ApplicationDetailsService {
  tabConfiguration: TabConfig[];

  constructor(private store: StoreService) {}

  applicationConfig(application: Application): TabConfig[] {
    if (!application?.journeyStatus) {
      application.journeyStatus = {};
    }
    this.tabConfiguration = [
      {
        journeyStage: 'workReport',
        title: 'Payroll Data',
        dataMessage: this.getDataMessage('workReport', 'Payroll Data', application),
        qaId: 'payroll-data-tab',
        isTabVisible: this.isTabVisible(DepartmentFeatures.WorkReport),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.WorkReport),
        isDataComplete:
          application?.journeyStatus?.workReport === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('workReport', application),
        tooltipText: 'work report',
        route: APP_ROUTES.payroll,
      },
      {
        journeyStage: 'openBanking',
        title: 'Open Banking',
        dataMessage: this.getDataMessage('openBanking', 'Open Banking', application),
        isTabVisible: this.isTabVisible(DepartmentFeatures.OpenBanking, true),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.OpenBanking),
        qaId: 'open-banking-tab',
        dropdown: [
          {
            title: 'Overview',
            value: 'overview',
            path: APP_ROUTES.overview,
          },
          {
            title: 'Affordability Passport',
            value: 'passport',
            path: APP_ROUTES.passport,
          },
          {
            title: 'Accounts',
            value: 'accounts',
            path: APP_ROUTES.accounts,
          },
        ],
        isDataComplete:
          application?.journeyStatus.openBanking === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('openBanking', application),
        tooltipText: 'open banking',
        route: APP_ROUTES.overview,
      },
      {
        journeyStage: 'openBanking',
        title: 'Overview',
        dataMessage: this.getDataMessage('openBanking', 'Open Banking', application),
        qaId: 'overview-tab',
        isTabVisible: this.isTabVisible(DepartmentFeatures.OpenBanking),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.OpenBanking),
        isDataComplete:
          application?.journeyStatus.openBanking === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('openBanking', application),
        tooltipText: 'open banking',
        route: APP_ROUTES.overview,
      },
      {
        journeyStage: 'openBanking',
        title: 'Affordability Passport',
        dataMessage: this.getDataMessage('openBanking', 'Open Banking', application),
        qaId: 'passport-tab',
        isTabVisible: this.isTabVisible(DepartmentFeatures.OpenBanking),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.OpenBanking),
        isDataComplete:
          application?.journeyStatus.openBanking === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('openBanking', application),
        tooltipText: 'open banking',
        route: APP_ROUTES.passport,
      },
      {
        journeyStage: 'openBanking',
        title: 'Accounts',
        dataMessage: this.getDataMessage('openBanking', 'Open Banking', application),

        qaId: 'accounts-tab',
        isTabVisible: this.isTabVisible(DepartmentFeatures.OpenBanking),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.OpenBanking),
        isDataComplete:
          application?.journeyStatus.openBanking === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('openBanking', application),
        tooltipText: 'open banking',
        route: APP_ROUTES.accounts,
      },
      {
        journeyStage: 'idDoc',
        title: 'ID Document Check',
        dataMessage: this.getDataMessage('idDoc', 'ID Document Check', application),
        qaId: 'id-doc-tab',
        isTabVisible: this.isTabVisible(DepartmentFeatures.IdDocVerificationPassport),
        isEnabledForDepartment: this.store
          .get<Department>(STORE_FIELDS.department)
          ?.features?.includes(DepartmentFeatures.IdDocVerificationPassport),
        isDataComplete:
          application?.journeyStatus.idDoc === StepDataStatus.Completed ||
          this.isCompletedAndDataSharedLegacyMode('idDoc', application),
        tooltipText: 'ID document',
        route: APP_ROUTES.idDocVerification,
      },
    ];

    return this.tabConfiguration;
  }

  isCompletedAndDataSharedLegacyMode(journeyStage: string, application: Application): boolean {
    if (application?.journeyStatus && Object.keys(application.journeyStatus).length !== 0) {
      return false;
    }

    const stage: string = journeyStage.charAt(0).toUpperCase() + journeyStage.slice(1);
    const isDataShared: boolean = application[`share${stage}Data`];
    const journeyStatus: StepData = application.journeyStatus;
    const isLegacyApplication: boolean = !journeyStatus.hasOwnProperty(journeyStage);
    return isLegacyApplication && CompletedApplicationStatus.has(application.status) && isDataShared;
  }

  getDataMessage(journeyStage: string, title: string, application: Application): string {
    const journeyStatus: StepData = application?.journeyStatus;
    const journeyStageForLegacy = journeyStage === 'creditReport' ? 'creditSummary' : journeyStage;
    if (journeyStatus?.hasOwnProperty(journeyStage)) {
      switch (journeyStatus[journeyStage]) {
        case StepDataStatus.Skipped:
        case StepDataStatus.ConsentDeclined: {
          return `${title} ${title.includes('Data') ? '' : 'data '}is not available.`;
        }
        case StepDataStatus.Completed: {
          return '';
        }
      }
      return `${title} ${title.includes('Data') ? '' : 'data '}is not available.`;
    }
    // If none of above, check legacy mode
    else if (this.isCompletedAndDataSharedLegacyMode(journeyStageForLegacy, application)) {
      return '';
    } else {
      return `${title} ${title.includes('Data') ? '' : 'data '}is not available.`;
    }
  }

  isTabVisible(feature: string, obGroup = false) {
    const department: Department = this.store.get(STORE_FIELDS.department);
    if (feature === DepartmentFeatures.OpenBanking) {
      if (obGroup) {
        return department?.features.includes(feature) && !this.isSingleFeatureOnly(feature);
      }
      return this.isSingleFeatureOnly(feature);
    }
    return department?.features.includes(feature);
  }

  isSingleFeatureOnly(feature: string): boolean {
    const department: Department = this.store.get(STORE_FIELDS.department);
    const allFeaturesForConsideration: string[] = [
      DepartmentFeatures.WorkReport,
      DepartmentFeatures.OpenBanking,
      DepartmentFeatures.IdDocVerificationPassport,
    ];
    const foundFeaturesInDepartment = department?.features.filter((deptFeature: string) =>
      allFeaturesForConsideration.includes(deptFeature),
    );
    return foundFeaturesInDepartment?.length === 1 && foundFeaturesInDepartment.includes(feature);
  }
}
